import { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import { Objective } from '../../utils/models/Objective';
import { ObjectiveType } from '../Objectives/ObjectiveTypeOperations';
import ObjectiveTypeSelector from './ObjectiveTypeSelector';

interface Props {
  isEditable?: boolean;
  canAddChild?: boolean;
  setShowChildren?: (boolean) => void;
  setShowDeleteModal: (boolean) => void;
  setShowCopyToNextPeriodModal: (boolean) => void;
  setShowDisconnectModal?: (boolean) => void;
  addChild: (Objective) => void;
  value: Objective;
  setValue: (any) => void;
  userEditableObjectiveTypes: ObjectiveType[];
  organizationObjectiveTypes: ObjectiveType[];
  parentTypeId: number | null | undefined;
  onChangeType?: (typeId: number | null) => void;
  onClickAddTag?: () => void;
}

const ObjectiveItemMoreControls: FC<Props> = ({
  isEditable,
  canAddChild = true,
  setShowChildren,
  setShowDeleteModal,
  setShowCopyToNextPeriodModal,
  setShowDisconnectModal,
  addChild,
  value,
  setValue,
  userEditableObjectiveTypes,
  organizationObjectiveTypes,
  parentTypeId,
  onChangeType,
  onClickAddTag,
}) => {
  const { formatMessage } = useIntl();
  const childrenTypeIds = useMemo(
    () => value.children?.map((c) => c.objective_type) ?? [],
    [value.children]
  );
  const [showExtraControls, setShowExtraControls] = useState(false);
  const toggle = useCallback(
    () => setShowExtraControls((prevState) => !prevState),
    []
  );

  const handleOnClickAddSubObjective = useCallback(
    () => addChild({ value, setValue, setShowChildren }),
    [addChild, setShowChildren, setValue, value]
  );
  const handleOnClickDeleteObjective = useCallback(
    () => setShowDeleteModal(true),
    [setShowDeleteModal]
  );

  const handleOnClickCopyToNextPeriod = useCallback(
    () => setShowCopyToNextPeriodModal(true),
    [setShowCopyToNextPeriodModal]
  );
  const handleOnClickDisconnectFromParent = useCallback(
    () => setShowDisconnectModal?.(true),
    [setShowDisconnectModal]
  );
  const output = (
    <>
      {isEditable && (
        <>
          <Dropdown isOpen={showExtraControls} toggle={toggle} direction="end">
            <DropdownToggle tag="button" className="focusable-element">
              <span
                className="btn btn-sm btn-outline-secondary p-0 d-inline-flex align-items-center justify-content-center"
                id={`add-child-button-${value.key}`}
                aria-label={formatMessage({
                  id: 'app.views.person.personal_objective_item.more_actions.aria_label',
                  defaultMessage: 'More actions button',
                })}
                style={{
                  width: '1.75rem',
                  height: '1.75rem',
                }}
              >
                <i className="fe fe-more-vertical" />
              </span>
            </DropdownToggle>
            <DropdownMenu className="pt-2 pb-2" end>
              {/* No objective type is valid under this parent, so creating a
                  child would only be rejected by the backend. */}
              {canAddChild && (
                <DropdownItem
                  id={`add-child-button-${value.key}`}
                  onClick={handleOnClickAddSubObjective}
                >
                  <i className="pe-2 fe fe-plus" />
                  <FormattedMessage
                    id="app.views.person.objective_item_add_delete_controls.add_a_subobjective"
                    defaultMessage="Add a subobjective"
                  />
                </DropdownItem>
              )}
              {onClickAddTag && (
                <DropdownItem
                  id={`add-tag-button-${value.key}`}
                  onClick={onClickAddTag}
                >
                  <i className="pe-2 fe fe-tag" />
                  <FormattedMessage
                    id="app.views.person.objective_item_more_controls.add_tag"
                    defaultMessage="Add tag"
                  />
                </DropdownItem>
              )}
              <DropdownItem
                id={`copy-to-next-period-button-${value.key}`}
                onClick={handleOnClickCopyToNextPeriod}
              >
                <i className="pe-2 fe fe-copy" />
                <FormattedMessage
                  id="app.views.person.objective_item_add_delete_controls.copy_to_next_period"
                  defaultMessage="Copy this objective to the next period"
                />
              </DropdownItem>
              {setShowDisconnectModal && (
                <DropdownItem
                  id={`disconnect-from-parent-button-${value.key}`}
                  onClick={handleOnClickDisconnectFromParent}
                >
                  <i className="pe-2 fe fe-link-2" />
                  <FormattedMessage
                    id="app.views.person.objective_item_more_controls.disconnect_from_parent"
                    defaultMessage="Disconnect from parent objective"
                  />
                </DropdownItem>
              )}
              <DropdownItem
                id={`delete-button-${value.key}`}
                onClick={handleOnClickDeleteObjective}
              >
                <i className="pe-2 fe fe-trash" />
                <FormattedMessage
                  id="app.views.person.objective_item_add_delete_controls.delete"
                  defaultMessage="Delete this objective"
                />
              </DropdownItem>
              {onChangeType && (
                <ObjectiveTypeSelector
                  userEditableObjectiveTypes={userEditableObjectiveTypes}
                  currentTypeId={value.objective_type}
                  onChangeType={onChangeType}
                  childrenTypeIds={childrenTypeIds}
                  parentTypeId={parentTypeId}
                  organizationObjectiveTypes={organizationObjectiveTypes}
                />
              )}
            </DropdownMenu>
          </Dropdown>
        </>
      )}
    </>
  );
  return output;
};

export default ObjectiveItemMoreControls;
